import React, { useState, useEffect, useRef } from 'react';
import { Maximize2, Copy, Check, Settings2 } from 'lucide-react';
import { useConsent } from '@/hooks/use-consent';
import { resolveAdTagHtml } from '@/lib/ad-url';

export interface AdSlotConfig {
  id: string;
  name: string;
  width: number;
  height: number;
  tag?: string;
  label: string;
}

interface GalleryAdSlotProps {
  config: AdSlotConfig;
  onConfigureTag: (slotId: string) => void;
  isEditing: boolean;
  className?: string;
  /** Fill the full viewport width (used for full-bleed slots like full-width-header). */
  unconstrained?: boolean;
  /** Fill 100% of the parent container width (used for between-article and similar inline slots). */
  fullWidth?: boolean;
  /** For overlay (1×1) slots: inject the ad script into this element instead of
   *  the nested adContainerRef. Cinema ads use `thisScript.parentNode.insertBefore`
   *  to place their iframe — they must run as a direct child of a top-level element
   *  (e.g. a div prepended to <body>) so the iframe isn't trapped in a tiny container. */
  scriptTarget?: HTMLElement | null;
}

function injectTag(tag: string, target: HTMLElement) {
  tag = resolveAdTagHtml(tag);
  target.innerHTML = '';
  const temp = document.createElement('div');
  temp.innerHTML = tag;
  Array.from(temp.childNodes).forEach(node => {
    if ((node as Element).nodeName === 'SCRIPT') {
      const orig = node as HTMLScriptElement;
      const script = document.createElement('script');
      Array.from(orig.attributes).forEach(attr => script.setAttribute(attr.name, attr.value));
      script.textContent = orig.textContent;
      target.appendChild(script);
    } else {
      target.appendChild(node.cloneNode(true));
    }
  });
}

const GalleryAdSlot: React.FC<GalleryAdSlotProps> = ({ config, onConfigureTag, isEditing, className = '', unconstrained = false, fullWidth = false, scriptTarget }) => {
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const adContainerRef = useRef<HTMLDivElement>(null);

  const hasTag = !!config.tag && config.tag.trim().length > 0;
  const isOverlaySlot = config.width === 1 && config.height === 1;

  // Inject ad tag HTML+scripts directly into the container.
  // Direct injection gives ad scripts full DOM access — they run in the publisher
  // iframe context and can expand, break out, and manipulate the page freely.
  //
  // For overlay slots with a scriptTarget, inject into the target element instead
  // of the nested adContainerRef — Cinema ads rely on thisScript.parentNode being
  // a direct body child so their iframe is inserted at the right DOM level.
  const { ready: consentReady, signal: consentSignal } = useConsent();
  useEffect(() => {
    if (!consentReady) return; // wait so the first request carries the right GDPR signal
    if (isOverlaySlot && scriptTarget) {
      if (!hasTag || !config.tag) { scriptTarget.innerHTML = ''; return; }
      injectTag(config.tag, scriptTarget);
      return () => { scriptTarget.innerHTML = ''; };
    }

    const container = adContainerRef.current;
    if (!container) return;
    if (!hasTag || !config.tag) {
      container.innerHTML = '';
      return;
    }
    injectTag(config.tag, container);
    return () => { container.innerHTML = ''; };
  }, [config.tag, hasTag, isOverlaySlot, scriptTarget, consentReady, consentSignal]);

  const handleCopyId = () => {
    navigator.clipboard.writeText(config.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // 1×1 overlay/adhesion slots — zero-height anchor, ad renders via position:absolute/fixed
  if (isOverlaySlot) {
    return (
      <div className={`relative ${className}`} style={{ height: isEditing ? 'auto' : 0, overflow: 'visible' }}>
        <div style={{ position: 'absolute', width: 1, height: 1, overflow: 'visible', top: 0, left: 0, zIndex: 9999 }}>
          <div ref={adContainerRef} style={{ position: 'absolute' }} />
        </div>
        {isEditing && (
          <div className="py-1 px-2">
            <button
              onClick={() => onConfigureTag(config.id)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full border transition-colors ${
                hasTag
                  ? 'text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                  : 'text-[#635bff] bg-[#635bff]/10 border-[#635bff]/20 hover:bg-[#635bff]/20'
              }`}
              style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
            >
              <Maximize2 className="w-2.5 h-2.5" />
              {config.name}
              {hasTag && <span className="ml-1 text-emerald-500">●</span>}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`relative group ${className}`}
      style={{ maxWidth: '100%' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`relative transition-all duration-200 ${
          isEditing
            ? 'border-2 border-dashed border-[#635bff]/40 hover:border-[#635bff] rounded-sm'
            : hasTag ? '' : 'border border-dashed border-black/10 rounded-sm'
        }`}
        style={{
          width: '100%',
          ...(unconstrained ? { maxWidth: '100vw', overflow: 'hidden' } : fullWidth ? { maxWidth: '100%' } : { maxWidth: `min(${config.width}px, 100%)`, margin: '0 auto' }),
          ...(hasTag ? {} : { aspectRatio: `${config.width}/${config.height}` }),
        }}
      >
        {hasTag ? (
          <div ref={adContainerRef} style={{ width: '100%' }} />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-neutral-50">
            <div className="flex flex-col items-center gap-2 text-center px-4">
              <p
                className="text-[10px] uppercase tracking-[0.15em] text-black/25"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                Advertisement
              </p>
              <p
                className="text-[10px] text-black/15 font-mono"
                style={{ fontFamily: 'ui-monospace, monospace' }}
              >
                {config.width} &times; {config.height}
              </p>
              {isEditing && (
                <button
                  onClick={() => onConfigureTag(config.id)}
                  className="mt-1 px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] font-medium text-[#635bff] bg-[#635bff]/8 hover:bg-[#635bff]/15 rounded transition-colors"
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                >
                  Add Tag
                </button>
              )}
            </div>
          </div>
        )}

        {isEditing && isHovered && (
          <div className="absolute inset-0 bg-[#635bff]/5 backdrop-blur-[1px] flex items-center justify-center z-10">
            <div className="flex items-center gap-2">
              <button
                onClick={() => onConfigureTag(config.id)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-[#635bff] hover:bg-[#635bff]/90 rounded shadow-lg transition-colors"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                <Settings2 className="w-3.5 h-3.5" />
                {hasTag ? 'Edit Tag' : 'Add Tag'}
              </button>
              <button
                onClick={handleCopyId}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-700 bg-white hover:bg-slate-50 rounded shadow-lg border border-slate-200 transition-colors"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                Copy ID
              </button>
            </div>
          </div>
        )}
      </div>

      {isEditing && (
        <div className="absolute -top-2.5 left-3 z-20">
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#635bff] bg-[#635bff]/10 border border-[#635bff]/20 rounded-full"
            style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
          >
            <Maximize2 className="w-2.5 h-2.5" />
            {config.name}
          </span>
        </div>
      )}
    </div>
  );
};

export default GalleryAdSlot;
